export interface Assignment {
  id: string
  title: string
  description: string
  githubUrl: string
  technologies: string[]
  status: 'completed' | 'in-progress'
  image?: string
}

export interface Project {
  id: string
  title: string
  description: string
  longDescription: string
  image: string
  technologies: string[]
  category: 'defi' | 'ml' | 'dashboard' | 'web3' | 'fintech' | 'other' | 'publication'
  status: 'completed' | 'in-progress' | 'archived' | 'prototype' | 'published'
  featured: boolean
  links: {
    live?: string
    github?: string
    caseStudy?: string
    demo?: string
    amazon?: string
  }
  problem: string
  solution: string
  result: string
  screenshots: string[]
  timeline: string
  team?: string
  challenges: string[]
  learnings: string[]
  assignments?: Assignment[]
  award?: {
    amount: string
    organization: string
    program: string
  }
  certification?: {
    type: string
    verified: boolean
  }
  partnership?: {
    organization: string
    url: string
    description: string
  }
}

export const projects: Project[] = [
    {
        id: "eigencloud-development",
        title: "EigenCloud Development Team (Kalshi) - NYU BnF",
        description: "Building decentralized services in Ethereum's restaking economy with $25B+ in staked assets.",
        longDescription:
          "This semester, we're working with EigenCloud, the leading player in Ethereum's new restaking economy, which now secures over $25 billion in staked assets. We'll be building a decentralized service that blends finance, AI, and distributed systems - hands-on work that demands stitching together multiple components and thinking hard about incentives and usability. What makes this project unique is that we're not doing it alone: we're receiving direct support from the EigenCloud team and will be working closely with them to co-develop a product. As a team, we've tackled full-stack applications and cross-chain identity tools in past semesters (Microsoft, QuickNode), and now we're bringing that experience into EigenCloud to explore how restaking can power the next generation of financial and AI-driven infrastructure.",
        image: "/Kalshi.png",
        technologies: [
          "Ethereum",
          "Restaking",
          "Solidity",
          "TypeScript",
          "React",
          "Node.js",
          "AI/ML",
          "Distributed Systems"
        ],
        category: "web3",
        status: "completed",
        featured: true,
        links: {
          // Add links when available
        },
        problem:
          "The restaking economy needs innovative decentralized services that can leverage $25B+ in staked assets to power next-generation financial and AI infrastructure.",
        solution:
          "Building a decentralized service that blends finance, AI, and distributed systems, working directly with EigenCloud team to co-develop products that explore how restaking can power future infrastructure.",
        result:
          "Completed with direct support from EigenCloud team. Successfully built on past experience with Microsoft and QuickNode partnerships to create innovative restaking-powered solutions.",
        screenshots: [
          "/Kalshi.png"
        ],
        timeline: "Current Semester",
        team: "NYU Development Team",
        challenges: [
          "Integrating multiple complex components (finance, AI, distributed systems)",
          "Designing proper incentives and usability for restaking economy",
          "Coordinating with EigenCloud team for co-development"
        ],
        learnings: [
          "Leveraging $25B+ in staked assets for innovative applications",
          "Building in the rapidly evolving restaking economy",
          "Direct partnership development with leading blockchain infrastructure companies"
        ],
        partnership: {
          organization: "Kalshi",
          url: "https://kalshi.com/",
          description: "Partnership with Kalshi for decentralized service development"
        }
      },
  {
    id: "labscoutr",
    title: "LabScoutr",
    description: "Rate-my-lab discovery platform for students to find research labs and mentors.",
    longDescription:
      "LabScoutr helps students discover and evaluate university research labs and advisors. It combines a clean directory with ratings, structured reviews (mentorship, culture, workload), and lightweight metrics so undergrads can make informed choices. The MVP focuses on fast search, clear lab profiles, and trustworthy submissions.",
    image: "/LabScoutr.png",
    technologies: [
      "React",
      "TypeScript",
      "Vite",
      "Tailwind CSS",
      "Node.js",
      "Express",
      "MongoDB Atlas",
      "OpenAlex API" // swap/expand as needed
    ],
    category: "other",
    status: "completed",
    featured: true,
    links: {
      live: "https://labscoutr.com",
      // github: "https://github.com/yourusername/labscoutr", // add if public
      //caseStudy: "/projects/labscoutr-case-study"            // optional
    },
    problem:
      "Students struggle to assess lab fit—information is scattered, informal, or outdated, and mentorship quality is hard to gauge.",
    solution:
      "A centralized directory of labs and advisors with structured reviews, clear metrics, and filters, so students can quickly shortlist good matches.",
    result:
      "MVP launched at labscoutr.com; early users exploring lab profiles and submitting reviews. Iterating on verification, moderation, and search.",
    screenshots: [
      "/LabScoutr.png"
    ],
    timeline: "Ongoing",
    team: "Founder-led",
    challenges: [
      "Normalizing faculty/lab data across departments and schools",
      "Balancing honest reviews with verification and moderation",
      "Designing filters and ranking signals that feel fair and useful"
    ],
    learnings: [
      "Students value mentorship and expectations clarity more than raw metrics",
      "Simple, explainable ranking beats opaque scoring for trust",
      "Tight data contracts make ingestion and UI rendering far easier"
    ],
    award: {
      amount: "$2000",
      organization: "THINK, DO, THRIVE Grant Program",
      program: "Burton D. Morgan Foundation and Veale Institute for Entrepreneurship"
    },
    certification: {
      type: "Certified Non-Profit",
      verified: true
    }
  },
  {
    id: "ada-voice-assistant",
    title: "First Place in NYU Pulse Hackathon - ADA - Voice Assistant for Elderly",
    description: "Voice-enabled AI companion for elderly care with continuous conversations and family contact access.",
    longDescription:
      "A hackathon project building a hands-free voice assistant designed for elderly care. ADA provides continuous voice conversations using OpenAI GPT-4o-mini, automatic silence detection for seamless interaction, and quick-access family contact buttons. All conversations are logged to MongoDB and text files for family review. The UI is optimized for elderly users with large buttons, high contrast, and simple design.",
    image: "/ADADashboard.png",
    technologies: [
      "Node.js",
      "Express",
      "MongoDB",
      "OpenAI GPT-4o-mini",
      "Web Speech API",
      "HTML/CSS/JavaScript"
    ],
    category: "other",
    status: "completed",
    featured: false,
    links: {
      demo: "https://example.com/ada-demo-video", // Add actual demo video URL
      github: "https://github.com/lljw9999/hackaton",
    },
    problem:
      "Elderly individuals need accessible companionship and easy ways to contact family, but traditional interfaces are too complex and require constant button interaction.",
    solution:
      "A fully hands-free voice assistant that enables natural conversation with automatic silence detection, plus one-tap family contact buttons. Conversations are automatically logged for family peace of mind.",
    result:
      "Hackathon prototype completed with working voice interaction, automatic conversation logging, and family contact integration. Optimized for ~0.8-2.5 second response times with graceful fallback handling.",
    screenshots: [
      "/ADADashboard.png"
    ],
    timeline: "Hackathon",
    team: "Hackathon Team",
    challenges: [
      "Implementing reliable silence detection with background noise handling",
      "Optimizing response times for natural conversation flow",
      "Designing elder-friendly UI with accessibility in mind"
    ],
    learnings: [
      "Continuous voice interaction requires careful silence detection tuning",
      "Elderly-focused design prioritizes simplicity and large touch targets",
      "Automatic logging provides family peace of mind without user friction"
    ],
    award: {
      amount: "First Place",
      organization: "NYU Pulse Hackathon",
      program: "NYU Pulse Hackathon"
    }
  },
  {
    id: "revsplit-finance",
    title: "RevSplit Finance",
    description: "Upcoming stock & reverse splits—clean, explainable, and trackable.",
    longDescription: "RevSplit Finance surfaces upcoming stock splits and reverse splits with clean summaries, dates, and context. The live splash showcases the product direction; the app focuses on readable tables, filters, and explainer cards so traders can quickly see what's changing and why.",
    image: "/RevSplitFinance.png",
    technologies: [
      "React",
      "TypeScript",
      "Vite",
      "Tailwind CSS",
      "Node.js",
      "Firebase Hosting",
      "Cloud Functions",
      "MongoDB"
    ],
    category: "fintech",
    status: "completed",
    featured: true,
    links: {
      live: "https://revsplitfinance.com"
    },
    problem: "Corporate action info (like splits/reverse splits) is scattered and hard to scan quickly, especially for active traders.",
    solution: "A focused tracker that centralizes upcoming splits with plain-English context, filters, and a modern UI optimized for quick decisions.",
    result: "Public splash launched; core tracker and explainer UI in progress with a modern, fast front end.",
    screenshots: [
      "/RevSplitFinance.png"
    ],
    timeline: "Ongoing",
    team: "Founder-led",
    challenges: [
      "Reliable corporate-action data ingestion and normalization",
      "Designing concise explainers that add context without clutter",
      "Keeping the UI fast while supporting filtering and search"
    ],
    learnings: [
      "Productizing corporate actions for traders requires clarity over volume",
      "Small UI details (sorting, empty states) drive trust and usability",
      "Clean data contracts simplify both ingestion and rendering"
    ],
    certification: {
      type: "Certified LLC",
      verified: true
    }
  },
  {
        id: "chess-bet",
        title: "ChessBet — P2P Wagers for Online Chess",
        description: "Peer-to-peer chess wagers with automated result verification and safe escrow.",
        longDescription:
          "MVP for friendly wagers on chess.com/lichess matches. Players connect their accounts, post or accept bets, and funds are escrowed before play. When the game ends, webhooks verify the result and release payouts automatically. Includes basic anti-cheat signals (engine-like move spikes, abort/timeout patterns), dispute flow, and private lobbies for friends.",
        image: "chess-piece", // Using Lucide React chess piece icon
        technologies: [
          "React", "TypeScript", "Vite", "Tailwind CSS",
          "Node.js", "Express", "PostgreSQL", "Prisma", "Redis",
          "OAuth (Lichess/Chess.com)", "Webhooks",
          "Stripe (escrow/payouts)", "Cloudflare Pages/Workers"
        ],
        category: "defi",
        status: "in-progress",
        featured: true,
        links: {
          // No links - project in development
        },
        problem:
          "Casual wagers on online chess are messy: trust issues, manual scorekeeping, and payment friction.",
        solution:
          "Escrow funds up front, verify results via platform APIs, auto-settle payouts, and surface anti-cheat signals with an easy dispute path.",
        result:
          "MVP in development: account linking, offer/accept flow, sandbox escrow, and end-to-end result webhooks working locally; staging rollout next.",
        screenshots: [
          "/projects/chessbet-1.png",
          "/projects/chessbet-2.png"
        ],
        timeline: "MVP — Fall 2025",
        team: "Solo",
        challenges: [
          "Reliable, idempotent webhook processing",
          "Collusion/abort abuse and fair-play signals",
          "Payment/escrow compliance and KYC thresholds"
        ],
        learnings: [
          "Webhook idempotency & retries matter for money flows",
          "Clear UX around stakes, timers, and disputes reduces churn",
          "Rate limits & OAuth scopes shape the product surface"
        ]
      },
  {
    id: "certify",
    title: "Certify",
    description: "Searchable catalog of CS, finance, and cybersecurity certifications with clean rankings and filters.",
    longDescription:
      "Certify aggregates certifications across tech and finance (e.g., cloud, security, data, trading) into a single, fast directory. The MVP focuses on clear comparison: domain, level, cost, duration, exam vendor, and study resources—plus bookmarking and simple rankings so students can decide quickly.",
    image: "/Certify.png",
    technologies: [
      "React",
      "TypeScript",
      "Vite",
      "Tailwind CSS",
      "Firebase Hosting",
      "Firestore",
      "Cloud Functions",
      "Radix UI",
      "Framer Motion"
    ],
    category: "other",
    status: "completed",
    featured: true,
    links: {
      live: "https://certify-ranking-app.web.app/",
      github: "https://github.com/Arda-Dinc04/Certify",    // add if public
      // caseStudy: "/projects/certify-case-study"              // optional
    },
    problem:
      "Certification information is scattered and inconsistent across vendors, making it hard to compare value, prep time, and difficulty.",
    solution:
      "A unified, filterable directory with structured fields (domain, level, cost, format, estimated prep time, prerequisites) and concise detail pages with resources.",
    result:
      "MVP is live with a modern UI and fast search; iterating on data coverage, ranking signals, and contributor workflows.",
    screenshots: [
      "/Certify.png"
    ],
    timeline: "Ongoing",
    team: "Founder-led",
    challenges: [
      "Normalizing data models across many vendors and categories",
      "Designing a ranking signal that is useful but transparent",
      "Keeping listings fresh without manual overhead"
    ],
    learnings: [
      "Users care most about prerequisites, time-to-prepare, and total cost at a glance",
      "Simple, explainable filters outperform complex scoring in early versions",
      "Clear data contracts make ingestion and UI rendering far easier"
    ]
  },
  {
    id: "fin4903",
    title: "Special Topics in Finance (FIN4903)",
    description: "Favorite course—applied quantitative finance: factor models, backtesting, and risk management.",
    longDescription:
      "My favorite class at NYU. We covered practical quantitative finance: signal design, factor modeling, portfolio construction, and risk management. I built notebooks for data ingestion, feature engineering, walk-forward backtests, and performance diagnostics (alpha, Sharpe, drawdown). The repo tracks weekly labs and a final project.",
    image: "/FIN4093.png", // add a screenshot from a notebook/backtest chart
    technologies: [
      "Python", "Pandas", "NumPy", "scikit-learn",
      "Matplotlib/Plotly", "pandas-ta", "yfinance"
    ],
    category: "other",
    status: "completed",
    featured: false,
    links: {
      github: "https://github.com/Arda-Dinc04/Special-Topics-FIN4903"
    },
    problem:
      "Turn raw market data into testable signals and evaluate them rigorously without look-ahead bias.",
    solution:
      "Built a reproducible pipeline for data prep, factor construction, and walk-forward backtesting with clear metrics and plots.",
    result:
      "Weekly labs and a final project demonstrating signal performance and risk characteristics.",
    screenshots: [
      "/FIN4093.png"
    ],
    timeline: "Spring 2025",
    team: "Solo",
    challenges: [
      "Handling missing data and corporate actions in market data",
      "Preventing look-ahead bias in factor construction and backtesting",
      "Balancing model complexity with interpretability in factor models"
    ],
    learnings: [
      "Walk-forward validation is crucial for realistic backtesting",
      "Transaction costs significantly impact strategy performance",
      "Factor timing and regime detection improve model robustness"
    ],
    assignments: [
        {
        id: "A1",
        title: "Data Ingestion & EDA",
        description: "Market data collection, cleaning, and exploratory analysis with basic statistics and visualizations.",
        githubUrl: "https://github.com/Arda-Dinc04/Special-Topics-FIN4903/blob/main/A1/Assignment_1.ipynb",
        technologies: ["Python", "Pandas", "yfinance", "Matplotlib"],
        status: "completed",
        image: "/SpecialTopicsImages/A1.png"
      },
      {
        id: "A2",
        title: "Factor Construction",
        description: "Building momentum, mean reversion, and volatility factors with proper normalization and scaling.",
        githubUrl: "https://github.com/Arda-Dinc04/Special-Topics-FIN4903/blob/main/A2/A2Final_Files/Assignment_2_V0.ipynb",
        technologies: ["Python", "Pandas", "NumPy", "pandas-ta"],
        status: "completed",
        image: "/SpecialTopicsImages/A2.png"
      },
      {
        id: "A3",
        title: "Portfolio Construction",
        description: "Equal-weight, risk-parity, and factor-based portfolio construction with rebalancing logic.",
        githubUrl: "https://github.com/Arda-Dinc04/Special-Topics-FIN4903/blob/main/A3/A3Final%20Files/Assignmet_3.ipynb",
        technologies: ["Python", "Pandas", "NumPy", "scipy"],
        status: "completed",
        image: "/SpecialTopicsImages/A3.png"
      },
      {
        id: "A4",
        title: "Backtesting Framework",
        description: "Walk-forward backtesting with proper signal timing, transaction costs, and performance metrics.",
        githubUrl: "https://github.com/Arda-Dinc04/Special-Topics-FIN4903/blob/main/A4/Assignment_SVM.ipynb",
        technologies: ["Python", "Pandas", "NumPy", "Matplotlib"],
        status: "completed",
        image: "/SpecialTopicsImages/A4.png"
      },
      {
        id: "A5",
        title: "Risk Management",
        description: "VaR calculation, drawdown analysis, and position sizing with volatility targeting.",
        githubUrl: "https://github.com/Arda-Dinc04/Special-Topics-FIN4903/blob/main/A5/HW_PCA_Eigen_portfolios.ipynb",
        technologies: ["Python", "Pandas", "NumPy", "scipy"],
        status: "completed",
        image: "/SpecialTopicsImages/A5.png"
      },
      {
        id: "A6",
        title: "Performance Attribution",
        description: "Decomposing returns into factor exposures, alpha, and systematic risk components.",
        githubUrl: "https://github.com/Arda-Dinc04/Special-Topics-FIN4903/blob/main/A6/assignment_6.ipynb",
        technologies: ["Python", "Pandas", "NumPy", "scikit-learn"],
        status: "completed",
        image: "/SpecialTopicsImages/A6.png"
      },
      {
        id: "A7",
        title: "Regime Detection",
        description: "Identifying market regimes using clustering and regime-switching models for adaptive strategies.",
        githubUrl: "https://github.com/Arda-Dinc04/Special-Topics-FIN4903/blob/main/A7/Assignment7.ipynb",
        technologies: ["Python", "Pandas", "scikit-learn", "statsmodels"],
        status: "completed",
        image: "/SpecialTopicsImages/A7.png"
      },
      {
        id: "A8",
        title: "Transaction Costs",
        description: "Modeling market impact, bid-ask spreads, and implementation shortfall for realistic backtesting.",
        githubUrl: "https://github.com/Arda-Dinc04/Special-Topics-FIN4903/tree/main/A8",
        technologies: ["Python", "Pandas", "NumPy"],
        status: "completed",
        image: "/SpecialTopicsImages/A8.png"
      },
      {
        id: "A9",
        title: "Alternative Data",
        description: "Incorporating sentiment, news, and alternative data sources into factor models.",
        githubUrl: "https://github.com/Arda-Dinc04/Special-Topics-FIN4903/blob/main/A9/Convolution_model_Step_by_Step_v2a.ipynb",
        technologies: ["Python", "Pandas", "requests", "BeautifulSoup"],
        status: "completed",
        image: "/SpecialTopicsImages/A9.png"
      },
      {
        id: "A10",
        title: "Multi-Factor Model",
        description: "Comprehensive factor model combining momentum, value, quality, and low-vol factors with risk budgeting.",
        githubUrl: "https://github.com/Arda-Dinc04/Special-Topics-FIN4903/blob/main/A10/BuildingaRecurrentNeuralNetwork-StepbyStep-v3.ipynb",
        technologies: ["Python", "Pandas", "NumPy", "scikit-learn", "Matplotlib"],
        status: "completed",
        image: "/SpecialTopicsImages/A10.png"
      }
    ]
  },
  {
    id: "us-stocks-ta",
    title: "US Stock Market Technical Analysis",
    description: "Five-year technical dataset and LSTM-based ranking for the top 50 U.S. equities.",
    longDescription:
      "Built a reproducible pipeline that pulls 5 years of daily OHLCV for 50 large-cap U.S. stocks across 10 industries, computes core technical indicators (SMA50/200, RSI, MACD, ADX, Bollinger Bands), and trains/evaluates an LSTM per ticker. The app compares assets by directional accuracy and error metrics, then produces a composite score to rank symbols. Outputs clean CSVs per ticker and plots for actual vs. predicted closes.",
    image: "/LSTM.png", // replace with your screenshot
    technologies: [
      "Python",
      "Pandas",
      "NumPy",
      "TensorFlow/Keras (LSTM)",
      "scikit-learn",
      "pandas-ta / TA-Lib",
      "Matplotlib",
      "yfinance"
    ],
    category: "ml",
    status: "completed",
    featured: true,
    links: {
         github: "https://github.com/Arda-Dinc04/LSTM-TA-for-US-Emerging-Crypto-Markets",   // add if public
         caseStudy: "https://docs.google.com/presentation/d/1cns3LB7iK0vzKfNbOFo4o1M4O6DyEld4I_ianAxHz70/edit?usp=sharing"        // optional
    },
    problem:
      "Technical data and evaluations are scattered; comparing assets by signal quality is manual and inconsistent.",
    solution:
      "Automate data collection and indicator generation, fit LSTMs with walk-forward validation, and rank symbols by directional accuracy and error metrics using a composite score.",
    result:
      "Generated 50+ cleaned CSVs and per-ticker plots; produced an asset ranking table for quick comparison and further research.",
    screenshots: [
      "/projects/us-stocks-ta-plot.png",  // actual vs predicted (like your slide)
      "/projects/us-stocks-ta-table.png"  // ranking/metrics view
    ],
    timeline: "Completed (2025)",
    team: "Solo",
    challenges: [
      "Handling missing data, splits/dividends adjustments",
      "Preventing look-ahead leakage in sequence modeling",
      "Stabilizing training across heterogeneous tickers"
    ],
    learnings: [
      "Walk-forward evaluation and proper scaling are critical for time series",
      "Indicator edge effects require careful windowing",
      "Composite scoring clarifies which assets merit deeper modeling"
    ]
  },
  {
    id: "zktls-demo",
    title: "zkTLS Fan Loyalty Demo",
    description: "Private, verifiable fan engagement (streams, socials, events) bridged on-chain via zkTLS.",
    longDescription:
      "A demo dApp showing how zkTLS can onboard Web2 users to Web3 without wallets or doxxing. Fans prove activity (Spotify, TikTok, IG, ticket scans) through zkTLS; artists get a clean dashboard, rankings, and reward tools. On-chain, a ZKsync-optimized verifier contract checks proofs with low gas (Keccak precompile; memory-first reads). The UX uses a paymaster for gasless submissions and account-abstraction patterns.",
    image: "/DemodApp.png",            // replace with your screenshot
    technologies: [
      "ZKsync Era (zkEVM)",
      "Solidity",
      "Reclaim Protocol (proxy zkTLS)",
      "React",
      "TypeScript",
      "Vite",
      "Tailwind CSS",
      "Radix UI",
      "Framer Motion",
      "Cloud Functions / Firebase",
      "Foundry/Hardhat"
    ],
    category: "web3",
    status: "prototype",
    featured: true,
    links: {
      caseStudy: "https://docs.google.com/document/d/1-R49i6nLamSekNuv_xPax3yJ0vkquHBlxS_JRKxH_Rc/edit?usp=sharing"
    },
    problem:
      "Artists can’t identify real superfans across fragmented platforms; fans can’t privately prove loyalty; bot farms game rewards.",
    solution:
      "Use zkTLS to attest Web2 actions without revealing raw data. Verify proofs on ZKsync with gas-efficient contracts; reward genuine fans through a points + proof threshold system; abstract crypto via gasless, no-wallet UX.",
    result:
      "End-to-end prototype: proof intake → on-chain verification → analytics dashboard. ~10% verifier gas reduction via ZKsync Keccak precompile; smooth, non-crypto UX with paymaster.",
    screenshots: [
      "/DemodApp.png",            // use the dashboard image you sent
    ],
    timeline: "Completed (2025)",
    team: "NYU B&F ZKsync Research Team — Robin Choi, Ashley Chen, Arda Dinc, Raymond Zhang",
    challenges: [
      "Normalizing provider differences (MPC, proxy, TEE) into one verifier interface",
      "Gas and constraint size in verifier paths; avoiding calldata-heavy reads",
      "Sybil resistance & anti-gaming without hurting UX"
    ],
    learnings: [
      "ZKsync precompiles + memory-first reads matter for verifier gas",
      "Proxy zkTLS (Reclaim) enables fastest integration; MPC/TEE add trust trade-offs",
      "Paymasters + AA are key for non-crypto users (one-click flow)"
    ]
  },
  {
    id: "airbnb-project",
    title: "Airbnb Data Analysis",
    description: "Comprehensive analysis of Airbnb listing data with statistical insights and visualizations.",
    longDescription:
      "A data analysis project examining Airbnb listing patterns, pricing strategies, and market dynamics. Used Python for data cleaning, statistical analysis, and visualization to uncover insights about location preferences, seasonal trends, and pricing factors. The analysis includes correlation studies, geographic mapping, and predictive modeling for listing performance.",
    image: "/Airbnb.png",
    technologies: [
      "Python",
      "Pandas",
      "NumPy",
      "Matplotlib",
      "Seaborn",
      "Jupyter Notebook",
      "Scikit-learn",
      "Plotly"
    ],
    category: "ml",
    status: "completed",
    featured: true,
    links: {
      github: "https://github.com/Arda-Dinc04/Airbnb-Project/blob/main/DataSummary.ipynb"
    },
    problem:
      "Airbnb data is complex and multi-dimensional, making it difficult to identify patterns in pricing, location preferences, and listing performance without systematic analysis.",
    solution:
      "Conducted comprehensive exploratory data analysis using Python to clean, visualize, and model Airbnb listing data. Applied statistical methods to identify key factors affecting pricing and occupancy rates.",
    result:
      "Generated actionable insights about pricing strategies, location desirability, and seasonal trends. Created interactive visualizations and statistical models to predict listing performance.",
    screenshots: [
      "/Airbnb.png"
    ],
    timeline: "2024",
    team: "Solo",
    challenges: [
      "Handling missing data and outliers in large datasets",
      "Feature engineering for categorical variables and text data",
      "Creating meaningful visualizations for complex relationships"
    ],
    learnings: [
      "Data preprocessing is crucial for accurate analysis results",
      "Visualization techniques can reveal patterns not obvious in raw data",
      "Statistical modeling helps validate hypotheses about market behavior"
    ]
  },
  {
    id: "book-blockchain",
    title: "Blockchain & Cryptocurrency: Innovations & Implications",
    description: "An accessible, technically grounded guide to blockchain, crypto, and their real-world impact.",
    longDescription:
      "A concise book that bridges fundamentals and applications: consensus and networks, smart contracts, DeFi, NFTs, scaling (L2s, zk proofs), security, and regulation. Written for students and practitioners who want clear explanations, balanced risks/benefits, and practical case studies without hype.",
    image: "/book.jpg", // add your cover image
    technologies: ["Research", "Technical Writing", "Markdown/LaTeX", "Figma/Diagrams"],
    category: "publication",
    status: "published",
    featured: true,
    links: {
      amazon: "https://www.amazon.com/Blockchain-Cryptocurrency-Innovations-Implications-technology-ebook/dp/B0CWZJ3JQQ"
    },
    problem:
      "Most resources are either too high-level or too speculative, making it hard for readers to grasp what’s real, useful, and secure.",
    solution:
      "Offer a structured, plain-English walkthrough of the tech stack and the economics behind it, with objective coverage of opportunities and risks.",
    result:
      "Published on Amazon Kindle with a focus on clarity, diagrams, and actionable mental models for evaluating blockchain use cases.",
    screenshots: ["/book.jpg"], // optional interior spread
    timeline: "Published",
    team: "Solo",
    challenges: [
      "Balancing technical accuracy with accessibility",
      "Keeping content current in a fast-moving domain",
      "Explaining zk and scaling trade-offs clearly"
    ],
    learnings: [
      "Concrete case studies beat abstract narratives",
      "Security and UX are decisive for real adoption",
      "Policy and incentives shape technical design choices"
    ]
  }
  
]


export const projectCategories = [
  { id: 'all', label: 'All Projects', count: projects.length },
  { id: 'defi', label: 'DeFi', count: projects.filter(p => p.category === 'defi').length },
  { id: 'ml', label: 'Machine Learning', count: projects.filter(p => p.category === 'ml').length },
  { id: 'dashboard', label: 'Dashboards', count: projects.filter(p => p.category === 'dashboard').length },
  { id: 'web3', label: 'Web3', count: projects.filter(p => p.category === 'web3').length },
  { id: 'fintech', label: 'FinTech', count: projects.filter(p => p.category === 'fintech').length },
  { id: 'publication', label: 'Publications', count: projects.filter(p => p.category === 'publication').length },
  { id: 'other', label: 'Other', count: projects.filter(p => p.category === 'other').length }
]
